import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { productosAPI, proveedoresAPI, comprasAPI } from '../api/client';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { Search, Plus, Trash2, Save, Truck, ArrowLeft, X, Calculator, Package, FileText, ShoppingCart } from 'lucide-react';

const NuevaCompraPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [productos, setProductos] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [proveedor, setProveedor] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [busquedaProveedor, setBusquedaProveedor] = useState('');
  const [showSelectorProveedor, setShowSelectorProveedor] = useState(false);
  const [showNuevoProveedor, setShowNuevoProveedor] = useState(false);
  const [aplicaIva, setAplicaIva] = useState(true);
  const [formaPago, setFormaPago] = useState('efectivo');
  const [referenciaPago, setReferenciaPago] = useState('');
  const [notas, setNotas] = useState('');
  const [loading, setLoading] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState({
    nombre: '',
    telefono: '',
    email: '',
    direccion: ''
  });
  
  const searchInputRef = useRef(null);
  const { formatCurrency } = useConfig();

  useEffect(() => {
    loadProductos();
    loadProveedores();
  }, []);

  const loadProductos = async () => {
    try {
      const response = await productosAPI.getAll();
      if (response.data.success) {
        setProductos(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  const loadProveedores = async () => {
    try {
      const response = await proveedoresAPI.getAll();
      if (response.data.success) {
        setProveedores(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setProveedores([]);
    }
  };

  const buscarProducto = async () => {
    if (!busqueda) return;
    const producto = productos.find(p => 
      p.codigo_barras === busqueda || 
      p.sku === busqueda ||
      p.nombre.toLowerCase().includes(busqueda.toLowerCase())
    );

    if (producto) {
      agregarAlCarrito(producto);
      setBusqueda('');
      searchInputRef.current?.focus();
    } else {
      alert('Producto no encontrado');
    }
  };

  const agregarAlCarrito = (producto) => {
    const existe = carrito.find(item => item.id === producto.id);
    
    if (existe) {
      setCarrito(carrito.map(item =>
        item.id === producto.id
          ? { ...item, cantidad: item.cantidad + 1 }
          : item
      ));
    } else {
      setCarrito([...carrito, {
        ...producto,
        cantidad: 1,
        costo: producto.precio_compra || producto.precio_venta * 0.6 || 0
      }]);
    }
  };

  const guardarNuevoProveedor = async () => {
    if (!nuevoProveedor.nombre) {
      alert('El nombre del proveedor es obligatorio');
      return;
    }

    try {
      const response = await proveedoresAPI.create(nuevoProveedor);
      if (response.data.success) {
        const proveedorCreado = response.data.data;
        setProveedor(proveedorCreado);
        setProveedores([...proveedores, proveedorCreado]);
        setShowNuevoProveedor(false);
        setNuevoProveedor({ nombre: '', telefono: '', email: '', direccion: '' });
        alert('Proveedor creado exitosamente');
      }
    } catch (error) {
      console.error('Error al crear proveedor:', error);
      alert('Error al crear proveedor');
    }
  };

  const eliminarDelCarrito = (id) => {
    setCarrito(carrito.filter(item => item.id !== id));
  };

  const actualizarCantidad = (id, cantidad) => {
    if (cantidad <= 0) {
      eliminarDelCarrito(id);
      return;
    }
    setCarrito(carrito.map(item =>
      item.id === id ? { ...item, cantidad: parseFloat(cantidad) } : item
    ));
  };

  const actualizarCosto = (id, costo) => {
    setCarrito(carrito.map(item =>
      item.id === id ? { ...item, costo: parseFloat(costo) || 0 } : item
    ));
  };

  const calcularSubtotal = () => {
    return carrito.reduce((sum, item) => 
      sum + (item.costo * item.cantidad), 0
    );
  };

  const calcularImpuestos = () => {
    if (!aplicaIva) return 0;
    return calcularSubtotal() * 0.12; // IVA 12% Guatemala
  };

  const calcularTotal = () => {
    return calcularSubtotal() + calcularImpuestos();
  };

  const guardarCompra = async () => {
    if (carrito.length === 0) {
      alert('El carrito está vacío');
      return;
    }

    // Validar que todos los productos tengan costo
    const sinCosto = carrito.filter(item => !item.costo || item.costo <= 0);
    if (sinCosto.length > 0) {
      alert(`Hay ${sinCosto.length} producto(s) sin costo unitario. Por favor revisa los precios.`);
      return;
    }

    setLoading(true);

    const compra = {
      proveedor_id: proveedor?.id || null,
      items: carrito.map(item => ({
        producto_id: item.id,
        cantidad: item.cantidad,
        costo_unitario: item.costo,
        descuento: 0
      })),
      aplica_iva: aplicaIva,
      forma_pago: formaPago,
      referencia_pago: referenciaPago,
      notas: notas,
      descuento: 0
    };

    try {
      const response = await comprasAPI.create(compra);
      if (response.data.success) {
        alert(`✅ Compra registrada exitosamente\nFolio: ${response.data.data.folio}\nTotal: ${formatCurrency(response.data.data.total)}`);
        navigate('/compras');
      } else {
        alert('Error al registrar compra: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error al guardar compra:', error);
      alert('Error al registrar compra. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/compras')} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Nueva Compra</h1>
          </div>
          <button
            onClick={guardarCompra}
            className="btn-primary flex items-center gap-2"
            disabled={loading}
          >
            <Save className="w-5 h-5" />
            {loading ? 'Guardando...' : 'Guardar Compra'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Buscador */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && buscarProducto()}
                    placeholder="Buscar producto para comprar..."
                    className="input-field pl-10"
                  />
                </div>
                <button onClick={buscarProducto} className="btn-primary px-4">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Lista de productos */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-primary-600" />
                Productos Disponibles
              </h3>
              {busqueda && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                  {productos.filter(p => 
                    p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
                    p.codigo_barras?.includes(busqueda) ||
                    p.sku?.includes(busqueda)
                  ).map((producto) => (
                    <button
                      key={producto.id}
                      onClick={() => agregarAlCarrito(producto)}
                      className="p-3 border rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-colors text-left"
                    >
                      <p className="font-medium truncate">{producto.nombre}</p>
                      <p className="text-xs text-gray-500">SKU: {producto.sku}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Stock: <span className="font-bold">{producto.stock || 0}</span>
                      </p>
                      <p className="text-sm text-primary-600 font-bold">
                        Costo: {formatCurrency(producto.precio_compra || producto.precio_venta * 0.6)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {!busqueda && (
                <p className="text-gray-500 text-center py-8">
                  Escribe en el buscador para ver productos disponibles
                </p>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Proveedor */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Proveedor
                </h3>
                {!proveedor && (
                  <button 
                    onClick={() => setShowSelectorProveedor(true)}
                    className="text-primary-600 text-sm font-medium hover:underline"
                  >
                    Seleccionar
                  </button>
                )}
              </div>
              
              {proveedor ? (
                <div className="p-3 bg-blue-50 rounded-lg flex justify-between items-center">
                  <div>
                    <p className="font-medium">{proveedor.nombre}</p>
                    <p className="text-sm text-gray-600">{proveedor.telefono}</p>
                  </div>
                  <button 
                    onClick={() => setProveedor(null)}
                    className="text-red-600 hover:bg-red-100 p-1 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">No hay proveedor seleccionado</p>
              )}
            </div>

            {/* Carrito */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-semibold mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-primary-600" />
                  Items ({carrito.length})
                </span>
                {carrito.length > 0 && (
                  <button
                    onClick={() => setCarrito([])}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Limpiar todo
                  </button>
                )}
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {carrito.map((item) => (
                  <div key={item.id} className="p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <p className="font-medium text-sm">{item.nombre}</p>
                      <button
                        onClick={() => eliminarDelCarrito(item.id)}
                        className="text-red-600 hover:bg-red-100 p-1 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500">Cantidad</label>
                        <input
                          type="number"
                          value={item.cantidad}
                          onChange={(e) => actualizarCantidad(item.id, e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Costo Unit.</label>
                        <input
                          type="number"
                          value={item.costo}
                          onChange={(e) => actualizarCosto(item.id, e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-2 text-right font-bold text-sm text-primary-600">
                      Subtotal: {formatCurrency(item.costo * item.cantidad)}
                    </div>
                  </div>
                ))}
              </div>
              
              {carrito.length > 0 && (
                <div className="mt-4 pt-4 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(calcularSubtotal())}</span>
                  </div>
                  {aplicaIva && (
                    <div className="flex justify-between text-sm">
                      <span>IVA (12%):</span>
                      <span>{formatCurrency(calcularImpuestos())}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total:</span>
                    <span className="text-primary-600">{formatCurrency(calcularTotal())}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Opciones de compra */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary-600" />
                Opciones de Compra
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={aplicaIva}
                      onChange={(e) => setAplicaIva(e.target.checked)}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm">Aplicar IVA (12%)</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forma de Pago
                  </label>
                  <select
                    value={formaPago}
                    onChange={(e) => setFormaPago(e.target.value)}
                    className="w-full input-field"
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="cheque">Cheque</option>
                    <option value="credito">Crédito</option>
                  </select>
                </div>

                {formaPago !== 'efectivo' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referencia de Pago
                    </label>
                    <input
                      type="text"
                      value={referenciaPago}
                      onChange={(e) => setReferenciaPago(e.target.value)}
                      placeholder="Número de referencia"
                      className="w-full input-field"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Notas
                  </label>
                  <textarea
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Observaciones de la compra..."
                    className="w-full input-field"
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Selector Proveedor */}
      {showSelectorProveedor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Seleccionar Proveedor</h2>
              <button onClick={() => setShowSelectorProveedor(false)}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>
            <input
              type="text"
              value={busquedaProveedor}
              onChange={(e) => setBusquedaProveedor(e.target.value)}
              placeholder="Buscar proveedor..."
              className="input-field mb-4"
            />
            <div className="max-h-60 overflow-y-auto">
              {proveedores.filter(p => 
                p.nombre.toLowerCase().includes(busquedaProveedor.toLowerCase())
              ).map(p => (
                <button
                  key={p.id}
                  onClick={() => {
                    setProveedor(p);
                    setShowSelectorProveedor(false);
                  }}
                  className="w-full text-left p-3 hover:bg-gray-50 border-b"
                >
                  <p className="font-medium">{p.nombre}</p>
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setShowSelectorProveedor(false);
                setShowNuevoProveedor(true);
              }}
              className="w-full mt-4 btn-secondary"
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Crear Nuevo Proveedor
            </button>
          </div>
        </div>
      )}

      {/* Modal Nuevo Proveedor */}
      {showNuevoProveedor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Nuevo Proveedor</h2>
              <button onClick={() => setShowNuevoProveedor(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={nuevoProveedor.nombre}
                  onChange={(e) => setNuevoProveedor({...nuevoProveedor, nombre: e.target.value})}
                  className="w-full input-field"
                  placeholder="Nombre del proveedor"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="text"
                  value={nuevoProveedor.telefono}
                  onChange={(e) => setNuevoProveedor({...nuevoProveedor, telefono: e.target.value})}
                  className="w-full input-field"
                  placeholder="Número de teléfono"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={nuevoProveedor.email}
                  onChange={(e) => setNuevoProveedor({...nuevoProveedor, email: e.target.value})}
                  className="w-full input-field"
                  placeholder="correo@ejemplo.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <textarea
                  value={nuevoProveedor.direccion}
                  onChange={(e) => setNuevoProveedor({...nuevoProveedor, direccion: e.target.value})}
                  className="w-full input-field"
                  rows="2"
                  placeholder="Dirección del proveedor"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowNuevoProveedor(false)}
                className="flex-1 btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={guardarNuevoProveedor}
                className="flex-1 btn-primary"
              >
                <Save className="w-4 h-4 inline mr-2" />
                Guardar Proveedor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NuevaCompraPage;
